/*
 * Nutrition scorecard configuration.
 *
 * Edit the values below and refresh the page - no other file needs to change.
 * This file is loaded before the app's own script, so every number/label/colour
 * here drives the live calculation and the on-screen recommendations.
 *
 * Roles used for colour coding (actionColors / overallScoreColorBands) must be
 * one of: "success" (green), "warning" (amber), "danger" (red), "accent" (blue),
 * "pro" (purple), "neutral" (grey) - these map to the app's existing colour tiles.
 */
const SCORE_CONFIG = {

  // ---------------------------------------------------------------------
  // Personalized target formula inputs (Protein / Carb / Calorie targets)
  // ---------------------------------------------------------------------

  // BMI -> weight multiplier used in the Protein target formula.
  // Each row is [upperBoundExclusive, multiplier]. The last row's bound is
  // effectively "and above". BMI is weight(kg) / height(m)^2.
  bmiMultiplierBands: [
    [20, 0.8],
    [25, 1.1],
    [30, 1.3],
    [Infinity, 1.5]
  ],

  // Age -> age multiplier used in the Protein target formula.
  ageMultiplierBands: [
    [30, 1],
    [40, 1.025],
    [50, 1.05],
    [60, 1.075],
    [Infinity, 1.1]
  ],

  // Activity level -> activity multiplier used in the Protein target formula.
  // Keys must exactly match the "Activity level" dropdown on the basic info screen.
  activityMultiplier: {
    "Sedentary": 1,
    "Moderately Active": 1.2,
    "Highly Active": 1.4
  },

  // If the patient is on GLP1 therapy, this value is also considered when
  // picking the Protein weight multiplier (the highest of BMI / activity / this wins).
  glp1WeightMultiplier: 1.4,

  // Fraction of daily calories allocated to fat, used to derive the Carb target
  // (remaining calories after protein and fat go to carbs).
  fatFraction: {
    normal: 0.4,
    glp1: 0.25
  },

  // Flat "ideal" Carb ceiling in grams (the top-scoring band's upper limit).
  carbCeiling: {
    normal: 150,
    glp1: 200
  },

  // The worst Carb tier begins at this multiple of the computed carb target.
  carbWorstMultiplier: 1.15,

  // GLP1 patients get a calorie deficit: calorieTarget = BMR * this value.
  glp1CalorieDeficitMultiplier: 0.8,


  // ---------------------------------------------------------------------
  // Static scoring bands for metrics with no personalized formula.
  // Each entry is [lowerBoundExclusive, upperBoundInclusive, rating].
  // Fibre ratings are numeric (1-5); VM/Processed/Omega3/Magnesium use L/M/H.
  // ---------------------------------------------------------------------
  staticBands: {
    Fibre: [
      [0, 10, 1],
      [10, 20, 2],
      [20, 30, 3],
      [30, 50, 4],
      [50, 99999, 5]
    ],
    VM: [
      [-0.1, 3, "L"],
      [3, 6, "M"],
      [6, 99999, "H"]
    ],
    Processed: [
      [-0.1, 2, "L"],
      [2, 4, "M"],
      [4, 99999, "H"]
    ],
    Omega3: [
      [-0.1, 1.5, "L"],
      [1.5, 3, "M"],
      [3, 99999, "H"]
    ],
    Magnesium: [
      [-0.1, 400, "L"],
      [400, 800, "M"],
      [800, 99999, "H"]
    ]
  },

  // "Target" range shown on the card for the static-band metrics above
  // (Protein and Carb compute their own personalized range instead).
  targetRanges: {
    Fibre: [30, 50],
    Omega3: [1.5, 3],
    Magnesium: [400, 800]
  },


  // ---------------------------------------------------------------------
  // Overall score weights - should sum to 1.
  // Protein/Carb/Fibre apply to an already-doubled 0-10 score; VM/Processed/
  // Omega3/Magnesium apply to their L/M/H rating (see categoricalNumeric)
  // and are doubled inside the overall-score formula instead.
  // ---------------------------------------------------------------------
  weights: {
    Protein: 0.30,
    Carb: 0.25,
    Fibre: 0.15,
    VM: 0.10,
    Processed: 0.10,
    Omega3: 0.05,
    Magnesium: 0.05
  },

  // Numeric value substituted for each L/M/H rating when folded into the
  // overall score. Processed is inverted (L=best=5) since less processed food is better.
  categoricalNumeric: {
    VM: { L: 1, M: 3, H: 5 },
    Processed: { L: 5, M: 3, H: 1 },
    Omega3: { L: 1, M: 3, H: 5 },
    Magnesium: { L: 1, M: 3, H: 5 }
  },


  // ---------------------------------------------------------------------
  // Recommendation ("action") thresholds for the score-based metrics.
  // ---------------------------------------------------------------------
  actionThresholds: {
    // Protein/Carb/Fibre scores here are on the doubled 0-10 scale.
    protein: { maintainAtOrAbove: 4 },                          // else INCREASE
    carb: { maintainAtOrAbove: 4, optimizeAt: 3 },              // else REDUCE
    fibre: { maintainAtOrAbove: 10, optimizeAtOrAbove: 8 },     // else INCREASE
    // Calories compares consumed/target as a ratio.
    calories: { reduceAboveRatio: 1.05, maintainAtOrAboveRatio: 0.95 }  // else INCREASE
  },

  // Recommendation text per L/M/H rating, for the categorical metrics.
  // Processed food: less is better, so L (low processed intake) = MAINTAIN, H = REDUCE.
  // VM/Omega3/Magnesium are beneficial nutrients: more is better (up to H), so
  // L (a deficiency) = INCREASE, H (plenty) = MAINTAIN - the opposite shape from Processed.
  ratingActions: {
    VM: { H: "MAINTAIN", M: "OPTIMIZE", L: "INCREASE" },
    Processed: { H: "REDUCE", M: "OPTIMIZE", L: "MAINTAIN" },
    Omega3: { H: "MAINTAIN", M: "OPTIMIZE", L: "INCREASE" },
    Magnesium: { H: "MAINTAIN", M: "OPTIMIZE", L: "INCREASE" }
  },


  // ---------------------------------------------------------------------
  // Colour coding
  // ---------------------------------------------------------------------

  // Processed food's tile colour follows its rating directly as a traffic light
  // (H=most processed=worst=red, M=amber, L=least processed=best=green), rather
  // than the shared action-label colouring below - see ratingColors usage in the app.
  ratingColors: {
    Processed: { L: "success", M: "warning", H: "danger" }
  },

  // Recommendation label -> colour role for each metric tile.
  actionColors: {
    MAINTAIN: "success",
    OPTIMIZE: "success",
    INCREASE: "warning",
    REDUCE: "danger"
  },

  // Overall score tile border colour, by score (out of 10).
  // Each row is [maxScoreInclusive, role]; the last row covers everything above it.
  overallScoreColorBands: [
    [4, "danger"],
    [7, "warning"],
    [Infinity, "success"]
  ],

  // Default tile colour for each metric before any food has been entered.
  emptyStateColors: {
    protein: "success",
    carb: "warning",
    fibre: "pro",
    vm: "accent",
    processed: "danger",
    omega3: "pro",
    magnesium: "accent",
    calories: "warning"
  }
};
